import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { getAuth, deleteUser, sendPasswordResetEmail } from "firebase/auth";
import moment from "moment";
import { db } from "../../lib/firebase";
import "../../styles/profile.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Loader from "../../components/Loader";
import UserCards from "./UserCards";
import UserPendingPayment from "./UserPendingPayment";
import UserLoanDetails from "./UserLoanDetails";
import UserActiveTrade from "./UserTrades";
import UserTransactions from "./UserTransactions";

const Details = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [updatedUser, setUpdatedUser] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [balance, setBalance] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("User info");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(userData);
          setUpdatedUser(userData);

          const userTransactions = userData.transactions || [];
          const userBalance = userTransactions.reduce((total, transaction) => {
            return transaction.type === "credit"
              ? total + transaction.amount
              : total - transaction.amount;
          }, 0);
          setBalance(userBalance);
        } else {
          console.log("No such document!");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user: ", error);
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedUser({ ...updatedUser, [name]: value });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateDoc(doc(db, "users", userId), updatedUser);
      setUser(updatedUser);
      setEditMode(false);
      setSuccessMessage("Update successful!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setUploadError(`Error saving: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleForgotPassword = async () => {
    const auth = getAuth();
    if (!user || !user.email) {
      setUploadError("User email not found.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email);
      setSuccessMessage(
        "Password reset email sent successfully to " + user.email,
      );
      setTimeout(() => {
        setSuccessMessage("");
      }, 8000);
    } catch (error) {
      setUploadError(`Error sending password reset email: ${error.message}`);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const auth = getAuth();
      await deleteDoc(doc(db, "users", userId));
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === userId) {
          await deleteUser(currentUser);
        }
      } catch (authError) {
        console.log("Auth user deletion error:", authError);
      }
      navigate("/get-admin");
    } catch (error) {
      setUploadError(`Error deleting user: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <Loader />;
  if (!user)
    return (
      <div style={{ color: "white", textAlign: "center", marginTop: "50px" }}>
        User not found
      </div>
    );

  const tabStyle = (isActive) => ({
    padding: "10px 20px",
    cursor: "pointer",
    background: isActive ? "#D52A2A" : "transparent",
    color: isActive ? "#fff" : "#111",
    border: isActive ? "none" : "1px solid #333",
    borderRadius: "8px",
    whiteSpace: "nowrap",
    fontSize: "14px",
    textDecoration: "none",
    transition: "all 0.3s ease",
  });

  return (
    <div className="profile-page">
      <div className="profile-card">
        <div
          className="hide-scroll"
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "20px",
            overflowX: "auto",
            paddingBottom: "10px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>{`.hide-scroll::-webkit-scrollbar { display: none; }`}</style>
          <button
            onClick={() => setActiveTab("User info")}
            style={tabStyle(activeTab === "User info")}
          >
            User info
          </button>
          <button
            onClick={() => setActiveTab("Transactions")}
            style={tabStyle(activeTab === "Transactions")}
          >
            Transactions
          </button>
          {["Payments", "Trades", "Cards", "Loans"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={tabStyle(activeTab === tab)}
                >
                  {tab}
                </button>
              ))
}
        </div>

        {activeTab === "User info" && (
          <>
            <div className="profile-grid">
              <div className="profile-field">
                <div
                  className="profile-header-item"
                  style={{ marginBottom: "15px" }}
                >
                  <div>
                    <div
                      className="profile-value"
                      style={{ fontSize: "1.4rem", marginBottom: "4px" }}
                    >
                      {user.fullName}
                    </div>
                    <div
                      className="profile-value"
                      style={{ opacity: 0.7, fontSize: "0.9rem" }}
                    >
                      {user.email}
                    </div>
                  </div>
                  <i
                    onClick={handleForgotPassword}
                    title="Reset Password"
                    className="profile-reset-icon fas fa-repeat"
                    style={{ cursor: "pointer", color: "#aaa" }}
                  ></i>
                </div>
                <div
                  style={{
                    marginTop: "10px",
                    paddingTop: "15px",
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <b className="profile-label">Available Balance</b>
                  <div
                    className="profile-value"
                    style={{
                      fontSize: "1.6rem",
                      fontWeight: "bold",
                      color: "#4CAF50",
                    }}
                  >
                    $
                    {balance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                {uploadError && (
                  <p
                    className="profile-error"
                    style={{ color: "#D52A2A", marginTop: "10px" }}
                  >
                    {uploadError}
                  </p>
                )}
              </div>

              <div className="profile-field">
                <b className="profile-label">Transaction Report</b>
                {editMode ? (
                  <input
                    type="text"
                    name="report"
                    className="profile-input"
                    value={updatedUser.report || ""}
                    onChange={handleChange}
                  />
                ) : (
                  <div className="profile-value">
                    {user.report || "No Report Available"}
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3
                className="profile-section-title"
                style={{
                  marginTop: "30px",
                  marginBottom: "20px",
                  fontSize: "1.2rem",
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  paddingBottom: "10px",
                }}
              >
                User Details
              </h3>
              <div className="profile-grid">
                <div className="profile-field">
                  <b className="profile-label">Full Name</b>
                  {editMode ? (
                    <input
                      type="text"
                      className="profile-input"
                      name="fullName"
                      value={updatedUser.fullName || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="profile-value">{user.fullName}</div>
                  )}
                </div>
                <div className="profile-field">
                  <b className="profile-label">Account Number</b>
                  {editMode ? (
                    <input
                      type="text"
                      className="profile-input"
                      name="accountNumber"
                      value={updatedUser.accountNumber || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="profile-value">
                      {user.accountNumber || "N/A"}
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-grid">
                <div className="profile-field">
                  <b className="profile-label">Address</b>
                  {editMode ? (
                    <input
                      type="text"
                      name="address"
                      className="profile-input"
                      value={updatedUser.address || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="profile-value">{user.address || "N/A"}</div>
                  )}
                </div>
                <div className="profile-field">
                  <b className="profile-label">Gender</b>
                  {editMode ? (
                    <select
                      name="gender"
                      className="profile-input"
                      value={updatedUser.gender || ""}
                      onChange={handleChange}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  ) : (
                    <div
                      className="profile-value"
                      style={{ textTransform: "capitalize" }}
                    >
                      {user.gender || "N/A"}
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-grid">
                <div className="profile-field">
                  <b className="profile-label">Date of Birth</b>
                  {editMode ? (
                    <input
                      type="date"
                      className="profile-input"
                      name="dob"
                      value={
                        updatedUser.dob
                          ? moment(updatedUser.dob).format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="profile-value">
                      {user.dob
                        ? moment(user.dob).format("MMMM D, YYYY")
                        : "N/A"}
                    </div>
                  )}
                </div>
                <div className="profile-field">
                  <b className="profile-label">Phone Number</b>
                  {editMode ? (
                    <input
                      type="text"
                      className="profile-input"
                      name="phoneNumber"
                      value={updatedUser.phoneNumber || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="profile-value">
                      {user.phoneNumber || "Not Set"}
                    </div>
                  )}
                </div>
              </div>

              <div className="profile-grid">
                <div className="profile-field">
                  <b className="profile-label">Role</b>
                  {editMode ? (
                    <select
                      name="role"
                      className="profile-input"
                      value={updatedUser.role || "user"}
                      onChange={handleChange}
                    >
                      <option value="user">Regular User</option>
                      <option value="investor">Active Investor</option>
                      <option value="admin">Global Admin</option>
                      <option value="moderator">Funding Moderator</option>
                    </select>
                  ) : (
                    <div className="profile-value">{user.role || "user"}</div>
                  )}
                </div>
                <div className="profile-field">
                  <b className="profile-label">Special Code</b>
                  {editMode ? (
                    <input
                      type="text"
                      className="profile-input"
                      name="code"
                      value={updatedUser.code || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="profile-value">{user.code || "N/A"}</div>
                  )}
                </div>
              </div>

              <div className="profile-grid">
                <div className="profile-field">
                  <b className="profile-label">Group Status</b>
                  {editMode ? (
                    <select
                      name="group"
                      className="profile-input"
                      value={updatedUser.group || "activated"}
                      onChange={handleChange}
                    >
                      <option value="activated">Activated</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="deactivated">Deactivated</option>
                    </select>
                  ) : (
                    <div
                      className="profile-value"
                      style={{ textTransform: "capitalize" }}
                    >
                      {user.group || "activated"}
                    </div>
                  )}
                </div>
              </div>

              <div
                className="profile-btn-group"
                style={{
                  marginTop: "30px",
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {editMode ? (
                  <button
                    onClick={handleSave}
                    className="profile-btn"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                ) : (
                  <button
                    onClick={() => setEditMode(true)}
                    className="profile-btn"
                  >
                    Edit User Info
                  </button>
                )}
                {editMode ? (
                  <button
                    onClick={() => setEditMode(false)}
                    style={{
                      padding: "12px 24px",
                      background: "transparent",
                      color: "#aaa",
                      border: "1px solid #555",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                      transition: "all 0.3s",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = "rgba(255, 255, 255, 0.05)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = "transparent";
                    }}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting || isSaving}
                    style={{
                      padding: "12px 24px",
                      background: "transparent",
                      color: "#D52A2A",
                      border: "1px solid #D52A2A",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                      transition: "all 0.3s",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = "rgba(213, 42, 42, 0.1)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = "transparent";
                    }}
                  >
                    {isDeleting ? "Deleting ..." : "Delete User"}
                  </button>
                )}
              </div>

              {successMessage && (
                <div
                  className="profile-success"
                  style={{
                    marginTop: "20px",
                    color: "#4CAF50",
                    textAlign: "center",
                    fontWeight: "500",
                    background: "rgba(76, 175, 80, 0.1)",
                    padding: "10px",
                    borderRadius: "8px",
                  }}
                >
                  {successMessage}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "Cards" && <UserCards user={user} userId={userId} setUser={setUser} />}
        {activeTab === "Payments" && <UserPendingPayment user={user} userId={userId} setUser={setUser} />}
        {activeTab === "Loans" && <UserLoanDetails user={user} userId={userId} setUser={setUser} />}
        {activeTab === "Trades" && <UserActiveTrade user={user} userId={userId} setUser={setUser} />}
        {activeTab === "Transactions" && <UserTransactions user={user} userId={userId} setUser={setUser} />}
      </div>
    </div>
  );
};

export default Details;
